import React, { useState, useEffect, useCallback } from 'react';
import { Search, Activity, ChevronRight, Eye, X, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { apiService } from '../services/api';
import type { UsageSummaryResponse, RequestDetail } from '../types';
import { formatCurrency } from '../utils';

interface UserRow {
  userId: string;
  email: string | null;
  requests: number;
  totalTokens: number;
  costUsd: number;
}

function StatBadge({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg bg-gray-50 px-4 py-3">
      <p className="text-xs text-gray-500">{label}</p>
      <p className="mt-0.5 text-lg font-semibold text-gray-900">{value}</p>
    </div>
  );
}

export default function Usage() {
  const [days, setDays] = useState(30);
  const [query, setQuery] = useState('');
  const [summary, setSummary] = useState<UsageSummaryResponse | null>(null);
  const [selectedUser, setSelectedUser] = useState<{ id: string; detail: UsageSummaryResponse } | null>(null);
  const [loading, setLoading] = useState(true);
  const [userLoading, setUserLoading] = useState(false);
  const [requestDetail, setRequestDetail] = useState<RequestDetail | null>(null);
  const [requestLoading, setRequestLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiService.getUsageSummary(days);
      setSummary(data);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { void load(); }, [load]);

  const drillUser = useCallback(async (userId: string) => {
    setUserLoading(true);
    try {
      const detail = await apiService.getUserUsage(userId, days);
      setSelectedUser({ id: userId, detail });
    } finally {
      setUserLoading(false);
    }
  }, [days]);

  const openRequest = useCallback(async (requestId: string) => {
    if (!requestId) return;
    setRequestLoading(true);
    setRequestDetail(null);
    try {
      const data = await apiService.getRequestDetail(requestId);
      setRequestDetail(data);
    } finally {
      setRequestLoading(false);
    }
  }, []);

  const users: UserRow[] = (summary?.byUser ?? []) as UserRow[];
  const filtered = users.filter(u =>
    !query || (u.email ?? u.userId).toLowerCase().includes(query.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Usage</h1>
          <p className="mt-1 text-sm text-gray-500">Per-user request timeline and token consumption</p>
        </div>
        <div className="flex gap-2">
          {[7, 14, 30, 90].map(d => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`px-3 py-1 rounded-full text-xs font-medium ${days === d ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {/* System Summary */}
      {summary && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <StatBadge label="Total Requests" value={summary.totalRequests.toLocaleString()} />
          <StatBadge label="Success Rate" value={`${summary.successRate}%`} />
          <StatBadge label="Total Tokens" value={summary.totalTokens.toLocaleString()} />
          <StatBadge label="Est. Cost" value={formatCurrency(summary.totalCostUsd)} />
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* User List */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-3">
            <Activity size={16} className="text-indigo-500" />
            <h2 className="font-semibold text-gray-900">Users</h2>
            <div className="flex-1" />
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-2.5 text-gray-400" />
              <input
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Search email / uid"
                className="pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>
          {loading ? (
            <div className="p-8 text-center text-sm text-gray-400">Loading...</div>
          ) : (
            <ul className="divide-y divide-gray-50 max-h-[500px] overflow-y-auto">
              {filtered.map(u => (
                <li key={u.userId}>
                  <button
                    onClick={() => drillUser(u.userId)}
                    className={`w-full flex items-center justify-between px-5 py-3 text-left hover:bg-indigo-50 transition-colors ${selectedUser?.id === u.userId ? 'bg-indigo-50' : ''}`}
                  >
                    <div>
                      <p className="text-sm font-medium text-gray-800">{u.email ?? u.userId}</p>
                      <p className="text-xs text-gray-400">{u.userId}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-700">{u.requests.toLocaleString()} req</p>
                      <p className="text-xs text-gray-400">{formatCurrency(u.costUsd)}</p>
                    </div>
                    <ChevronRight size={14} className="ml-3 text-gray-300 flex-shrink-0" />
                  </button>
                </li>
              ))}
              {filtered.length === 0 && (
                <li className="px-5 py-8 text-center text-sm text-gray-400">No users found</li>
              )}
            </ul>
          )}
        </div>

        {/* User Detail Panel */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          {!selectedUser ? (
            <div className="flex items-center justify-center h-full min-h-[200px] text-sm text-gray-400">
              ← Select a user to drill in
            </div>
          ) : userLoading ? (
            <div className="flex items-center justify-center h-full min-h-[200px] text-sm text-gray-400">
              Loading...
            </div>
          ) : (
            <>
              <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                <h2 className="font-semibold text-gray-900 truncate">
                  {(selectedUser.detail as any)?.user?.email ?? selectedUser.id}
                </h2>
                <Link
                  to={`/users/${selectedUser.id}`}
                  className="text-xs text-indigo-600 hover:underline ml-3 flex-shrink-0"
                >
                  Full profile →
                </Link>
              </div>
              <div className="p-5 space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <StatBadge label="Requests" value={selectedUser.detail.totalRequests.toLocaleString()} />
                  <StatBadge label="Success Rate" value={`${selectedUser.detail.successRate}%`} />
                  <StatBadge label="Tokens" value={selectedUser.detail.totalTokens.toLocaleString()} />
                  <StatBadge label="Cost" value={formatCurrency(selectedUser.detail.totalCostUsd)} />
                </div>

                {/* Top models for this user */}
                {selectedUser.detail.byModel?.length > 0 && (
                  <div>
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Models</h3>
                    <div className="space-y-1">
                      {selectedUser.detail.byModel.slice(0, 5).map((m: any) => (
                        <div key={m.model} className="flex justify-between text-sm">
                          <span className="font-mono text-gray-700">{m.model}</span>
                          <span className="text-gray-500">{m.requests} req · {formatCurrency(m.costUsd)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Recent events */}
                {(selectedUser.detail as any)?.recentEvents?.length > 0 && (
                  <div>
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Recent Requests</h3>
                    <div className="space-y-1 max-h-48 overflow-y-auto">
                      {(selectedUser.detail as any).recentEvents.slice(0, 20).map((e: any) => (
                        <div key={e.id} className="flex justify-between items-center text-xs text-gray-500 border-b border-gray-50 pb-1">
                          <span className="font-mono truncate max-w-[120px]">{e.model}</span>
                          <span>{e.totalTokens} tok</span>
                          <span className={e.status < 400 ? 'text-green-600' : 'text-red-500'}>{e.status}</span>
                          <span>{e.latencyMs}ms</span>
                          <button
                            onClick={() => openRequest(e.requestId)}
                            title="View messages"
                            className="ml-1 p-0.5 rounded hover:bg-indigo-100 text-indigo-400 hover:text-indigo-600 transition-colors"
                          >
                            <Eye size={12} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Request Detail Modal */}
      {(requestLoading || requestDetail) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={() => setRequestDetail(null)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
            {/* Modal header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <h2 className="font-semibold text-gray-900">Request Detail</h2>
              <button onClick={() => setRequestDetail(null)} className="p-1 rounded hover:bg-gray-100 text-gray-400">
                <X size={16} />
              </button>
            </div>

            {requestLoading ? (
              <div className="flex items-center justify-center py-16 gap-2 text-gray-400">
                <Loader2 size={18} className="animate-spin" />
                <span className="text-sm">Loading…</span>
              </div>
            ) : requestDetail && (
              <div className="overflow-y-auto flex-1 px-5 py-4 space-y-4">
                {/* Metadata row */}
                <div className="flex flex-wrap gap-2 text-xs">
                  <span className="bg-gray-100 px-2 py-0.5 rounded font-mono">{requestDetail.requestModel}</span>
                  <span className={`px-2 py-0.5 rounded font-medium ${requestDetail.status < 400 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{requestDetail.status}</span>
                  <span className="bg-gray-100 px-2 py-0.5 rounded">{requestDetail.latencyMs}ms</span>
                  <span className="bg-gray-100 px-2 py-0.5 rounded">{requestDetail.totalTokens} tokens</span>
                  <span className="bg-gray-100 px-2 py-0.5 rounded">{requestDetail.promptTokens}↑ {requestDetail.completionTokens}↓</span>
                  {requestDetail.isEstimated && <span className="bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded">estimated</span>}
                </div>

                {/* Messages */}
                {requestDetail.requestMessages && requestDetail.requestMessages.length > 0 ? (
                  <div>
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Conversation</h3>
                    <div className="space-y-3">
                      {requestDetail.requestMessages.map((msg, i) => (
                        <div key={i} className={`rounded-lg px-4 py-3 text-sm ${
                          msg.role === 'system'
                            ? 'bg-gray-100 text-gray-600 border border-gray-200'
                            : msg.role === 'user'
                            ? 'bg-indigo-50 text-gray-800 border border-indigo-100 ml-4'
                            : 'bg-green-50 text-gray-800 border border-green-100 mr-4'
                        }`}>
                          <span className="text-[10px] font-semibold uppercase tracking-wider opacity-60 block mb-1">{msg.role}</span>
                          <p className="whitespace-pre-wrap break-words">{msg.content}</p>
                        </div>
                      ))}
                      {/* Assistant response */}
                      {requestDetail.responseContent && (
                        <div className="bg-green-50 text-gray-800 border border-green-100 rounded-lg px-4 py-3 text-sm mr-4">
                          <span className="text-[10px] font-semibold uppercase tracking-wider opacity-60 block mb-1">assistant</span>
                          <p className="whitespace-pre-wrap break-words">{requestDetail.responseContent}</p>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="text-sm text-gray-400 text-center py-6">
                    Message content not available for this request.<br />
                    <span className="text-xs">Only requests made after the latest deploy will include message content.</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
